import { Box, Button, Menu, Tooltip, Typography } from "@mui/material";
import { useContext, useState } from "react";
import { PostTrackVersionResponse, VersionState } from "../../proto/api_pb";
import { styles } from "../../pages/Tracks/styles";
import { StatusAndNoteProps, VMap, versionState } from "./types";
import { sortMenuStyles } from "./styles";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { postTrack, postTrackVersion } from "../../services/trackService";
import { RpcError } from "grpc-web";
import { toast } from "react-toastify";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
} from "../../contexts";

export const StatusAndNote: React.FC<StatusAndNoteProps> = ({
  version,
  setIsVStateReadyForMastering,
  handleFetchTrackVersions,
  handleRefreshTracks,
  track,
  handleApplyFilterAndSort
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { getValidAccessToken } = useGetValidAccessToken();
  const open = Boolean(anchorEl);
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { isEnterpriseAccount, enterprise } = useContext(UserDetailsContext);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const updateVersionState = async (state: VMap) => {
    if (version) {
      const updatedVersion = version;

      updatedVersion.setVersionState(state);
      const postTrackVersionResponse = await postTrackVersion(
        version,
        await getValidAccessToken(),
        userId,
        isEnterpriseAccount ? enterprise?.id : undefined
      );
      if (postTrackVersionResponse instanceof RpcError) {
        console.log(postTrackVersionResponse);
      }
      if (postTrackVersionResponse instanceof PostTrackVersionResponse) {
        if (
          setIsVStateReadyForMastering &&
          postTrackVersionResponse.getTrackVersion()?.getVersionState() ===
          VersionState.VERSION_STATE_READY_FOR_MASTERING
        ) {
          setIsVStateReadyForMastering(true);
        } else if (setIsVStateReadyForMastering) {
          setIsVStateReadyForMastering(false);
        } else {
          if (handleFetchTrackVersions) handleFetchTrackVersions();
        }
      }
      toast.info("Status updated successfully");
      const r = await postTrack(track, await getValidAccessToken(), userId);
      if (r instanceof RpcError) {
        console.log(r);
      }
      handleClose();
    }
    handleRefreshTracks();
    handleApplyFilterAndSort()
  };

  return (
    <div>
      <Button
        sx={{
          borderRadius: "var(--radius-full, 9999px)",
          textTransform: "none",
        }}
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={(e) => {
          e.stopPropagation();
          handleClick(e);
        }}
      >
        <Box
          sx={{
            ...styles.noteContainer,
            ...(version.getVersionState() ===
              VersionState.VERSION_STATE_READY_FOR_MASTERING &&
              styles.masteringNoteContainer),
            ...(version.getVersionState() ===
              VersionState.VERSION_STATE_READY_FOR_PUBLISHING &&
              styles.publishingNoteContainer),
            ...(version.getVersionState() ===
              VersionState.VERSION_STATE_COMPLETED &&
              styles.completeNoteContainer),
            ...(version.getVersionState() ===
              VersionState.VERSION_STATE_READY_FOR_MIXING &&
              styles.mixingNoteContainer),
            ...(version.getVersionState() ===
              VersionState.VERSION_STATE_UNSPECIFIED &&
              styles.unspecifiedNoteContainer),
            ...(version.getVersionState() ===
              VersionState.VERSION_STATE_PRE_PRODUCTION &&
              styles.preProductionNoteContainer),
          }}
        >
          <Typography sx={{ fontSize: "14px", textTransform: "none" }}>
            {version && versionState[version.getVersionState()]}
            <ArrowDropDownIcon />
          </Typography>
        </Box>
      </Button>
      <Menu
        onClick={(e) => e.stopPropagation()}
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        sx={sortMenuStyles.menu}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <Box sx={styles.noteMenuContainer}>
          <Button
            sx={styles.noteButton}
            onClick={() =>
              updateVersionState(VersionState.VERSION_STATE_UNSPECIFIED)
            }
          >
            <Box sx={styles.unspecifiedNoteContainer}>
              <Typography sx={styles.typography}>Select status</Typography>
            </Box>
          </Button>
          <Button
            sx={styles.noteButton}
            onClick={() =>
              updateVersionState(VersionState.VERSION_STATE_READY_FOR_RECORDING)
            }
          >
            <Box sx={styles.noteContainer}>
              <Typography sx={styles.typography}>
                Ready for recording
              </Typography>
            </Box>
          </Button>
          <Button
            sx={styles.noteButton}
            onClick={() =>
              updateVersionState(VersionState.VERSION_STATE_READY_FOR_MASTERING)
            }
          >
            <Box sx={styles.masteringNoteContainer}>
              <Typography sx={styles.typography}>
                Ready for mastering
              </Typography>
            </Box>
          </Button>

          <Button
            sx={styles.noteButton}
            onClick={() =>
              updateVersionState(VersionState.VERSION_STATE_READY_FOR_MIXING)
            }
          >
            <Box sx={styles.mixingNoteContainer}>
              <Typography sx={styles.typography}>Ready for mixing</Typography>
            </Box>
          </Button>

          <Button
            sx={styles.noteButton}
            onClick={() =>
              updateVersionState(
                VersionState.VERSION_STATE_READY_FOR_PUBLISHING
              )
            }
          >
            <Box sx={styles.publishingNoteContainer}>
              <Typography sx={styles.typography}>
                Ready for publishing
              </Typography>
            </Box>
          </Button>
          <Button
            sx={styles.noteButton}
            onClick={() =>
              updateVersionState(VersionState.VERSION_STATE_COMPLETED)
            }
          >
            <Box sx={styles.completeNoteContainer}>
              <Typography sx={styles.typography}>Completed track</Typography>
            </Box>
          </Button>
          <Button
            sx={styles.noteButton}
            onClick={() =>
              updateVersionState(VersionState.VERSION_STATE_PRE_PRODUCTION)
            }
          >
            <Box sx={styles.preProductionNoteContainer}>
              <Typography sx={styles.typography}>Pre production</Typography>
            </Box>
          </Button>
        </Box>
      </Menu>
      <Tooltip
        disableHoverListener={version.getLabel().length < 26}
        title={version?.getLabel()}
        arrow
      >
        <Typography
          sx={{
            fontSize: "14px",
            marginLeft: "10px",
            paddingLeft: "8px",
            maxWidth: "180px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {version?.getLabel()}
        </Typography>
      </Tooltip>
    </div>
  );
};
